import { useState, useRef, useEffect, useCallback } from "react";
import Joyride from "react-joyride";
import spanishOptionsJoyRide from "@helpers/spanishOptionsJoyRide/spanishOptionsJoyRide";
import { EJERCICIO_REFERENCIAS } from "./EjerciciosCatalogo";

// ─── Tour de bienvenida — se muestra una vez por navegador (cookie, sobrevive a localStorage.clear()) ───
function getExcelTourSeen() {
  return document.cookie.split(";").some((c) => c.trim().startsWith("xex_tour_seen="));
}
function markExcelTourSeen() {
  const exp = new Date();
  exp.setFullYear(exp.getFullYear() + 2);
  document.cookie = `xex_tour_seen=1; expires=${exp.toUTCString()}; path=/; SameSite=Lax`;
}

const EXCEL_TOUR_STEPS = [
  {
    target: ".xex-topbar-xp",
    content: "Aquí ves tus puntos de experiencia (XP) y tu porcentaje de avance general del ejercicio.",
    disableBeacon: true,
  },
  {
    target: ".xex-ribbon",
    content: "Esta es la cinta de opciones. Selecciona una celda editable y usa estos botones para poner negrita, cursiva, subrayado o cambiar la alineación del texto.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: ".xex-formula-bar",
    content: "La barra de fórmulas muestra el contenido real de la celda seleccionada (por ejemplo =B4*C4). Aquí también puedes escribir o corregir tu fórmula.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: ".xex-grid",
    content: "Esta es tu hoja de cálculo. Haz clic en una celda para seleccionarla y doble clic (o Enter) para editarla. Al seleccionar una celda con datos verás un cuadrito verde en su esquina inferior: arrástralo hacia abajo para copiar la fórmula a otras filas ajustando las referencias automáticamente.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: ".xex-sidebar-objectives",
    content: "Aquí están los objetivos del ejercicio, organizados por fases. Usa 💡 Pista si te atoras y 📖 Concepto para repasar la explicación de cada uno una vez completado.",
    disableBeacon: true,
    placement: "left",
  },
  {
    target: ".exc-container",
    content: "Estás listo para llevar tus habilidades en Excel al siguiente nivel.",
    disableBeacon: true,
    placement: "bottom",
  },
];

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║               EXCEL EXERCISE TEMPLATE — SCHEMA DE CONFIGURACIÓN          ║
// ╠═══════════════════════════════════════════════════════════════════════════╣
// ║  Cada ejercicio es un objeto LESSON_CONFIG con la siguiente estructura:  ║
// ║                                                                           ║
// ║  META                                                                     ║
// ║    title          string   — Título del ejercicio (topbar)                ║
// ║    subtitle       string   — Nivel · Módulo (topbar)                      ║
// ║    badge          string   — Emoji en el modal de introducción             ║
// ║    description    string   — Descripción del ejercicio (intro)             ║
// ║    completionText string   — Texto al completar todos los objetivos        ║
// ║                                                                           ║
// ║  GRID                                                                     ║
// ║    cols           string[] — Letras de columnas, ej. ["A","B","C","D"]    ║
// ║    rows           number   — Cantidad de filas visibles                   ║
// ║    colWidths      object   — Ancho en px por columna, ej. { A:160, B:110} ║
// ║                                                                           ║
// ║  DATA                                                                     ║
// ║    initialData    object   — Datos precargados { A1:"Texto", B2:"=..." }  ║
// ║    lockedCells    string[] — Celdas no editables, ej. ["A1","B1"]         ║
// ║                                                                           ║
// ║  INTRO MODAL                                                              ║
// ║    introTitle     string   — Título del modal de introducción              ║
// ║    introFunctions array    — [[formula, descripción], ...]                 ║
// ║                                                                           ║
// ║  CELL STYLING  (opcional)                                                 ║
// ║    getCellStyle   fn|null  — (cellId, cells) => { bg, color,              ║
// ║                               fontWeight, fontSize, italic, align }       ║
// ║                                                                           ║
// ║  CHART  (opcional)                                                        ║
// ║    chart.enabled  bool     — Muestra u oculta la gráfica                  ║
// ║    chart.title    string   — Título de la gráfica                         ║
// ║    chart.bars     array    — [{ label, cellId, color }]                   ║
// ║    chart.emptyMsg string   — Mensaje cuando aún no hay datos               ║
// ║    chart.format   fn       — (value) => string, formatea etiquetas         ║
// ║                                                                           ║
// ║  OBJECTIVES                                                               ║
// ║    objectives[]                                                            ║
// ║      id           string   — Identificador único                           ║
// ║      phase        number   — Número de fase (agrupa objetivos)             ║
// ║      phaseLabel   string   — Nombre de la fase                            ║
// ║      label        string   — Título corto del objetivo                    ║
// ║      description  string   — Instrucción detallada                        ║
// ║      hint         string   — Pista que se muestra al solicitarla          ║
// ║      concept      string   — Explicación didáctica al completar           ║
// ║      xp           number   — Puntos XP al completar                       ║
// ║      check        fn       — (cells) => boolean, valida la celda          ║
// ╚═══════════════════════════════════════════════════════════════════════════╝


// ═══════════════════════════════════════════════════════════════════════════════
//  EJERCICIO 1 — FÓRMULAS BÁSICAS  (Nivel Básico)
// ═══════════════════════════════════════════════════════════════════════════════
const EJERCICIO_BASICO = {
  title: "Fórmulas Básicas de Excel",
  subtitle: "Nivel Básico · Módulo 1",
  badge: "🧮",
  description: "Aprende las operaciones fundamentales: suma, resta, multiplicación y división usando referencias de celdas en lugar de números fijos.",
  completionText: "¡Dominaste las operaciones básicas! Ahora puedes construir fórmulas con referencias de celda.",
  introTitle: "Tus primeras fórmulas",

  cols: ["A","B","C","D","E"],
  rows: 16,
  colWidths: { A:170, B:110, C:110, D:130, E:110 },

  lockedCells: ["A1","A3","B3","C3","D3","A4","A5","A6","A7","B4","B5","B6","B7","C4","C5","C6","C7","A9","A10","A11","A12","A14","B14","A15","B15"],

  introFunctions: [
    ["=A1+B1",    "Suma el valor de A1 y B1"],
    ["=A1-B1",    "Resta B1 de A1"],
    ["=A1*B1",    "Multiplica A1 por B1"],
    ["=A1/B1",    "Divide A1 entre B1"],
    ["=SUMA(A1:A5)", "Suma un rango completo de celdas"],
  ],

  initialData: {
    A1:"OPERACIONES BÁSICAS CON EXCEL",
    A3:"Producto",    B3:"Precio",  C3:"Cantidad",  D3:"Subtotal",
    A4:"Cuaderno",   B4:"25",      C4:"4",
    A5:"Pluma",      B5:"8",       C5:"10",
    A6:"Regla",      B6:"15",      C6:"3",
    A7:"Mochila",    B7:"350",     C7:"2",
    A9:"CALCULADORA RÁPIDA",
    A10:"Valor A →", B10:"120",
    A11:"Valor B →", B11:"45",
    A12:"Operación", A14:"Diferencia (A-B)", B14:"",
    A15:"Producto (A×B)", B15:"",
    A12:"Operaciones en D12:D15 →",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1)); const col = id[0];
    if (id==="A1") return { bg:"#1e3a5f", color:"white", fontWeight:800, fontSize:13 };
    if (row===3)   return { bg:"#dbeafe", color:"#1e40af", fontWeight:700, fontSize:12 };
    if (col==="D" && row>=4 && row<=7) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#eff6ff":"#f8fafc", color:v?.startsWith("=")?"#1e40af":"#94a3b8", fontWeight:600 };
    }
    if (id==="A9" || id==="A12") return { bg:"#1e3a5f", color:"white", fontWeight:700, fontSize:11 };
    if (["D12","D13","D14","D15"].includes(id)) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#eff6ff":"#f8fafc", color:v?.startsWith("=")?"#1e40af":"#94a3b8", fontWeight:600 };
    }
    return null;
  },

  chart: {
    enabled: true,
    title: "Subtotales por Producto",
    emptyMsg: "Completa las fórmulas en D4:D7 para ver la gráfica",
    bars: [
      { label:"Cuaderno", cellId:"D4", color:"#3b82f6" },
      { label:"Pluma",    cellId:"D5", color:"#8b5cf6" },
      { label:"Regla",    cellId:"D6", color:"#f59e0b" },
      { label:"Mochila",  cellId:"D7", color:"#ef4444" },
    ],
    format: (v) => `$${Number(v).toLocaleString("es-MX")}`,
  },

  objectives: [
    {
      id:"b1", phase:1, phaseLabel:"Multiplicación",
      label:"Subtotal del Cuaderno",
      description:"En D4 multiplica Precio × Cantidad: =B4*C4",
      hint:"Las fórmulas siempre empiezan con = y usan referencias de celda (B4) en vez de números fijos",
      concept:"Usar referencias de celda (=B4*C4) en lugar de números (=25*4) permite que la fórmula se actualice automáticamente si cambias los datos.",
      xp:15, check: c => { const v=c["D4"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=b4*c4"||v==="=c4*b4"; },
    },
    {
      id:"b2", phase:1, phaseLabel:"Multiplicación",
      label:"Subtotales de Pluma, Regla y Mochila",
      description:"Completa D5, D6 y D7 con la fórmula de multiplicación para cada producto",
      hint:"=B5*C5 para Pluma, =B6*C6 para Regla, =B7*C7 para Mochila",
      concept:"Replicar el patrón de la fórmula cambiando solo el número de fila es la base del trabajo eficiente en Excel.",
      xp:20, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=b${r}*c${r}`||v===`=c${r}*b${r}`; };
        return ok("D5",5)&&ok("D6",6)&&ok("D7",7);
      },
    },
    {
      id:"b3", phase:2, phaseLabel:"Suma de rango",
      label:"Total general con SUMA()",
      description:"En D8 usa =SUMA(D4:D7) para sumar todos los subtotales",
      hint:"SUMA(D4:D7) suma todas las celdas del rango, desde D4 hasta D7",
      concept:"SUMA(rango) es mucho más eficiente que sumar celda por celda (=D4+D5+D6+D7), especialmente cuando hay muchas filas.",
      xp:20, check: c => { const v=c["D8"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=suma(d4:d7)"||v==="=sum(d4:d7)"; },
    },
    {
      id:"b4", phase:3, phaseLabel:"Operaciones aritméticas",
      label:"Calcula la diferencia (A - B)",
      description:"En D14 resta el Valor B del Valor A: usa B10 y B11 como referencias",
      hint:"=B10-B11 restará 45 de 120. El resultado debe ser 75",
      concept:"La resta en Excel usa el operador - entre dos referencias de celda. Si cambias B10 o B11, D14 se recalcula solo.",
      xp:15, check: c => { const v=c["D14"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=b10-b11"; },
    },
    {
      id:"b5", phase:3, phaseLabel:"Operaciones aritméticas",
      label:"Calcula el producto (A × B)",
      description:"En D15 multiplica el Valor A por el Valor B",
      hint:"=B10*B11 multiplicará 120 × 45",
      concept:"Con estas 4 operaciones (+, -, *, /) puedes resolver la gran mayoría de cálculos cotidianos en Excel.",
      xp:15, check: c => { const v=c["D15"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=b10*b11"||v==="=b11*b10"; },
    },
  ],
};


// ═══════════════════════════════════════════════════════════════════════════════
//  EJERCICIO 2 — DASHBOARD DE VENTAS  (Nivel Intermedio)
// ═══════════════════════════════════════════════════════════════════════════════
const EJERCICIO_DASHBOARD = {
  title: "Dashboard de Ventas 2024",
  subtitle: "Nivel Intermedio · Módulo 5",
  badge: "📊",
  description: "Construye un tablero de control completo con datos de ventas regionales usando SUMA, PROMEDIO, MAX y MIN para obtener métricas clave.",
  completionText: "Dominaste las 4 fases del dashboard: estructura, totales, indicadores KPI y gran total.",
  introTitle: "Construye tu primer Dashboard",

  cols: ["A","B","C","D","E","F","G"],
  rows: 20,
  colWidths: { A:168, B:118, C:118, D:118, E:118, F:110, G:90 },

  lockedCells: ["A1","A3","B3","C3","D3","E3","A4","A5","A6","A7","B4","B5","B6","B7","C4","C5","C6","C7","D4","D5","D6","D7","E4","E5","E6","E7","A9","A10","A11","A13","A14","A15","A16","A17"],

  introFunctions: [
    ["=SUMA(rango)",     "Suma un conjunto de celdas"],
    ["=MAX(rango)",      "Encuentra el valor máximo"],
    ["=MIN(rango)",      "Encuentra el valor mínimo"],
    ["=PROMEDIO(rango)", "Calcula el promedio"],
    ["=CONTAR(rango)",   "Cuenta celdas con número"],
  ],

  initialData: {
    A1:"DASHBOARD DE VENTAS 2024",
    A3:"Región",   B3:"T1 (Ene-Mar)", C3:"T2 (Abr-Jun)", D3:"T3 (Jul-Sep)", E3:"T4 (Oct-Dic)",
    A4:"Norte",  B4:"142500", C4:"167800", D4:"198300", E4:"221000",
    A5:"Sur",    B5:"98700",  C5:"112400", D5:"134900", E5:"156200",
    A6:"Este",   B6:"201000", C6:"187600", D6:"215400", E6:"244800",
    A7:"Oeste",  B7:"76300",  C7:"89500",  D7:"103200", E7:"118600",
    A9:"MÉTRICAS CLAVE", A10:"Total anual por región →", A11:"Total por trimestre ↓",
    A13:"INDICADORES",
    A14:"Venta más alta (MAX)", A15:"Venta más baja (MIN)", A16:"Promedio general", A17:"Regiones activas",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1)); const col = id[0];
    if (id==="A1") return { bg:"#1d6f42", color:"white", fontWeight:800, fontSize:13 };
    if (row===3)   return { bg:"#d1fae5", color:"#064e3b", fontWeight:700, fontSize:12 };
    if (row===8)   return { bg:"#dcfce7", color:"#065f46", fontWeight:700, fontSize:12 };
    if (col==="F" && row>=3 && row<=8) return { bg:"#ecfdf5", color:"#065f46", fontWeight:600 };
    if ([9,13].includes(row) && col==="A") return { bg:"#1d6f42", color:"white", fontWeight:700, fontSize:11 };
    if (col==="B" && row>=14 && row<=17 && cells[id]?.startsWith("=")) return { bg:"#fefce8", color:"#92400e", fontWeight:700 };
    if (cells[id]?.startsWith("=")) return { bg:"#f0fdf4", color:"#166534", fontWeight:500 };
    return null;
  },

  chart: {
    enabled: true,
    title: "Total Anual por Región",
    emptyMsg: "Completa las fórmulas de totales (F4:F7) para ver la gráfica",
    bars: [
      { label:"Norte", cellId:"F4", color:"#1d6f42" },
      { label:"Sur",   cellId:"F5", color:"#3b82f6" },
      { label:"Este",  cellId:"F6", color:"#f59e0b" },
      { label:"Oeste", cellId:"F7", color:"#ef4444" },
    ],
    format: (v) => `${(Number(v)/1000).toFixed(0)}k`,
  },

  objectives: [
    { id:"d1", phase:1, phaseLabel:"Estructura",
      label:'Encabezado "Total" en F3',
      description:'Escribe "Total" en la celda F3',
      hint:"Haz clic en F3 y escribe: Total",
      concept:"Los dashboards siempre tienen columnas de totales para resumir cada fila de datos de un vistazo.",
      xp:10, check: c => c["F3"]?.trim().toLowerCase()==="total" },
    { id:"d2", phase:1, phaseLabel:"Estructura",
      label:"Total anual — Región Norte",
      description:"En F4 suma los 4 trimestres de Norte: =SUMA(B4:E4)",
      hint:"SUMA(B4:E4) suma todas las celdas desde B4 hasta E4 en una sola fórmula",
      concept:"SUMA(rango) es la función más usada en dashboards. Automatiza la suma de cualquier cantidad de celdas.",
      xp:15, check: c => { const v=c["F4"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=suma(b4:e4)"||v==="=sum(b4:e4)"; } },
    { id:"d3", phase:1, phaseLabel:"Estructura",
      label:"Totales para Sur, Este y Oeste",
      description:"Completa F5, F6 y F7 con SUMA para cada región",
      hint:"=SUMA(B5:E5), =SUMA(B6:E6), =SUMA(B7:E7)",
      concept:"Replicar fórmulas cambiando solo el número de fila es la técnica más eficiente en Excel.",
      xp:20, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=suma(b${r}:e${r})`||v===`=sum(b${r}:e${r})`; };
        return ok("F5",5)&&ok("F6",6)&&ok("F7",7);
      } },
    { id:"d4", phase:2, phaseLabel:"Totales por trimestre",
      label:"Total del trimestre T1",
      description:"En B8 suma las ventas T1 de todas las regiones: =SUMA(B4:B7)",
      hint:"Ahora sumamos en vertical (columna), de la fila 4 a la 7",
      concept:"Los dashboards muestran totales horizontales (por fila) Y verticales (por columna) para dar una visión completa.",
      xp:15, check: c => { const v=c["B8"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=suma(b4:b7)"||v==="=sum(b4:b7)"; } },
    { id:"d5", phase:2, phaseLabel:"Totales por trimestre",
      label:"Totales T2, T3 y T4",
      description:"Completa C8, D8 y E8 con la suma vertical de cada trimestre",
      hint:"=SUMA(C4:C7), =SUMA(D4:D7), =SUMA(E4:E7)",
      concept:"Con totales por columna identificas de un vistazo qué trimestre generó más ingresos.",
      xp:20, check: c => {
        const ok=(id,col)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=suma(${col}4:${col}7)`||v===`=sum(${col}4:${col}7)`; };
        return ok("C8","c")&&ok("D8","d")&&ok("E8","e");
      } },
    { id:"d6", phase:3, phaseLabel:"Indicadores KPI",
      label:"Venta más alta con MAX()",
      description:"En B14 encuentra el máximo de toda la tabla: =MAX(B4:E7)",
      hint:"MAX(rango) devuelve el número más grande dentro de un rango de celdas",
      concept:"MAX() identifica al líder. En un dashboard real este valor se resalta visualmente para que el gerente lo vea de inmediato.",
      xp:25, check: c => { const v=c["B14"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=max(b4:e7)"; } },
    { id:"d7", phase:3, phaseLabel:"Indicadores KPI",
      label:"Venta más baja con MIN()",
      description:"En B15 encuentra el mínimo de toda la tabla: =MIN(B4:E7)",
      hint:"MIN(rango) devuelve el número más pequeño. Útil para detectar áreas de oportunidad.",
      concept:"MIN() detecta el punto débil. Comparar MAX y MIN te dice qué tan desigual es el rendimiento entre regiones.",
      xp:25, check: c => { const v=c["B15"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=min(b4:e7)"; } },
    { id:"d8", phase:3, phaseLabel:"Indicadores KPI",
      label:"Promedio general con PROMEDIO()",
      description:"En B16 calcula el promedio de toda la tabla: =PROMEDIO(B4:E7)",
      hint:"También puedes usar =AVERAGE(B4:E7) en inglés",
      concept:"El promedio establece la línea base. Cualquier región por debajo del promedio necesita atención estratégica.",
      xp:25, check: c => { const v=c["B16"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=promedio(b4:e7)"||v==="=average(b4:e7)"; } },
    { id:"d9", phase:4, phaseLabel:"Gran Total",
      label:"Gran Total del año en F8",
      description:"En F8 escribe =SUMA(F4:F7) para obtener el gran total anual",
      hint:"Es la suma de todos los totales regionales — el KPI principal del dashboard",
      concept:"El Gran Total es la métrica más importante del tablero. Ahora tu dashboard está completo y funcional.",
      xp:30, check: c => { const v=c["F8"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=suma(f4:f7)"||v==="=sum(f4:f7)"; } },
  ],
};


// ═══════════════════════════════════════════════════════════════════════════════
//  EJERCICIO 3 — FUNCIÓN SI (IF)  (Nivel Avanzado)
// ═══════════════════════════════════════════════════════════════════════════════
const EJERCICIO_SI = {
  title: "Lógica Condicional con SI()",
  subtitle: "Nivel Avanzado · Módulo 8",
  badge: "🔀",
  description: "Aprende a usar la función SI para que Excel tome decisiones automáticas basadas en condiciones. Clasificarás estudiantes, calcularás bonos y aplicarás lógica anidada.",
  completionText: "¡Dominaste la función SI()! Ahora Excel puede tomar decisiones por ti automáticamente.",
  introTitle: "Excel que toma decisiones",

  cols: ["A","B","C","D","E","F"],
  rows: 18,
  colWidths: { A:150, B:110, C:130, D:150, E:150, F:110 },

  lockedCells: ["A1","A3","B3","C3","D3","A4","A5","A6","A7","A8","B4","B5","B6","B7","B8","A10","A11","B10","B11","A13","A14","A15","A16","A17","A18","B13","B14","B15","B16","B17","B18","C13","C14","C15","C16","C17","C18"],

  introFunctions: [
    ["=SI(cond, si_verdadero, si_falso)", "Evalúa una condición y devuelve un valor u otro"],
    ["=SI(A1>10, \"Alto\", \"Bajo\")",     "Si A1 es mayor a 10, escribe Alto; si no, Bajo"],
    ["=SI(A1>=60, \"Aprobado\", \"Reprobado\")", "Aprueba si la nota es 60 o más"],
    ["=Y(cond1, cond2)",                  "Verdadero solo si AMBAS condiciones se cumplen"],
    ["=O(cond1, cond2)",                  "Verdadero si AL MENOS UNA condición se cumple"],
  ],

  initialData: {
    A1:"EVALUACIÓN CON FUNCIÓN SI()",
    A3:"Estudiante", B3:"Calificación", C3:"Estado", D3:"Nivel",
    A4:"Ana García",   B4:"92",
    A5:"Luis Pérez",   B5:"58",
    A6:"María López",  B6:"75",
    A7:"Juan Ramos",   B7:"40",
    A8:"Sofía Torres", B8:"88",
    A10:"REGLAS DE EVALUACIÓN",
    A11:"≥ 90 = Excelente  |  ≥ 70 = Bueno  |  ≥ 60 = Aprobado  |  < 60 = Reprobado",
    A13:"TABLA DE BONOS",
    A14:"Vendedor", B14:"Ventas ($)", C14:"Meta ($)",
    A15:"Roberto",  B15:"85000",  C15:"80000",
    A16:"Carmen",   B16:"62000",  C16:"80000",
    A17:"Miguel",   B17:"95000",  C17:"80000",
    A18:"Daniela",  B18:"80500",  C18:"80000",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1)); const col = id[0];
    if (id==="A1") return { bg:"#4c1d95", color:"white", fontWeight:800, fontSize:13 };
    if (row===3)   return { bg:"#ede9fe", color:"#4c1d95", fontWeight:700, fontSize:12 };
    if (id==="A10"||id==="A13") return { bg:"#4c1d95", color:"white", fontWeight:700, fontSize:11 };
    if (id==="A11") return { bg:"#f5f3ff", color:"#5b21b6", fontSize:11, italic:true };
    if (row===14)  return { bg:"#ede9fe", color:"#4c1d95", fontWeight:700, fontSize:12 };
    if (col==="C" && row>=4 && row<=8) {
      const v = cells[id];
      if (!v) return { bg:"#f8fafc", color:"#94a3b8" };
      const ev = v.startsWith("=") ? null : v;
      if (ev) {
        if (ev.toLowerCase().includes("reprobado")) return { bg:"#fef2f2", color:"#dc2626", fontWeight:700 };
        if (ev.toLowerCase().includes("aprobado"))  return { bg:"#f0fdf4", color:"#16a34a", fontWeight:700 };
        return { bg:"#eff6ff", color:"#1d4ed8", fontWeight:700 };
      }
      return { bg:"#f5f3ff", color:"#5b21b6", fontWeight:600 };
    }
    if (col==="D" && row>=4 && row<=8) {
      const v = cells[id];
      if (!v) return { bg:"#f8fafc", color:"#94a3b8" };
      return { bg:"#f5f3ff", color:"#5b21b6", fontWeight:600 };
    }
    if (col==="D" && row>=15 && row<=18) {
      const v = cells[id];
      if (!v) return { bg:"#f8fafc", color:"#94a3b8" };
      return { bg:"#f5f3ff", color:"#5b21b6", fontWeight:600 };
    }
    return null;
  },

  chart: { enabled: false },

  objectives: [
    { id:"s1", phase:1, phaseLabel:"SI básico — Aprobado/Reprobado",
      label:"Estado de Ana García",
      description:'En C4 usa SI para mostrar "Aprobado" si B4>=60, si no "Reprobado"',
      hint:'=SI(B4>=60,"Aprobado","Reprobado")  — el texto va entre comillas',
      concept:'La función SI tiene 3 partes: =SI(condición, valor_si_verdadero, valor_si_falso). Las comillas son obligatorias para texto.',
      xp:20, check: c => { const v=c["C4"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=si(b4>=60,"aprobado","reprobado")'||v==='=if(b4>=60,"aprobado","reprobado")'; } },
    { id:"s2", phase:1, phaseLabel:"SI básico — Aprobado/Reprobado",
      label:"Estado de los demás estudiantes",
      description:"Aplica la misma lógica en C5, C6, C7 y C8 para cada estudiante",
      hint:"Misma fórmula, cambia solo el número de fila: =SI(B5>=60,...)",
      concept:"Una fórmula SI en cada fila evalúa a cada persona de forma independiente. Excel recalcula si cambias las calificaciones.",
      xp:25, check: c => {
        const ok = (id, r) => { const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=si(b${r}>=60,"aprobado","reprobado")`||v===`=if(b${r}>=60,"aprobado","reprobado")`; };
        return ok("C5",5)&&ok("C6",6)&&ok("C7",7)&&ok("C8",8);
      } },
    { id:"s3", phase:2, phaseLabel:"SI anidado — Niveles",
      label:"Nivel de Ana García (SI anidado)",
      description:'En D4 clasifica por nivel: ≥90="Excelente", ≥70="Bueno", ≥60="Aprobado", si no="Reprobado"',
      hint:'=SI(B4>=90,"Excelente",SI(B4>=70,"Bueno",SI(B4>=60,"Aprobado","Reprobado")))',
      concept:'Un SI anidado es un SI dentro de otro SI. Excel evalúa de mayor a menor: si la primera condición falla, prueba la siguiente.',
      xp:30, check: c => { const v=c["D4"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=si(b4>=90,"excelente",si(b4>=70,"bueno",si(b4>=60,"aprobado","reprobado")))'||v==='=if(b4>=90,"excelente",if(b4>=70,"bueno",if(b4>=60,"aprobado","reprobado")))'; } },
    { id:"s4", phase:2, phaseLabel:"SI anidado — Niveles",
      label:"Nivel de los demás estudiantes",
      description:"Aplica el SI anidado en D5, D6, D7 y D8",
      hint:"Misma fórmula del SI anidado, solo cambia el número de fila (B5, B6, B7, B8)",
      concept:"El SI anidado puede tener hasta 64 niveles, aunque con más de 3 es mejor usar BUSCARV o IFS para mayor claridad.",
      xp:30, check: c => {
        const ok = (id, r) => { const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=si(b${r}>=90,"excelente",si(b${r}>=70,"bueno",si(b${r}>=60,"aprobado","reprobado")))`||v===`=if(b${r}>=90,"excelente",if(b${r}>=70,"bueno",if(b${r}>=60,"aprobado","reprobado")))`; };
        return ok("D5",5)&&ok("D6",6)&&ok("D7",7)&&ok("D8",8);
      } },
    { id:"s5", phase:3, phaseLabel:"SI con comparación — Bonos",
      label:"¿Roberto recibe bono?",
      description:'En D15 determina si Roberto cumplió su meta: =SI(B15>=C15,"✓ Bono","✗ Sin bono")',
      hint:"Comparamos las ventas reales (B15) contra la meta (C15)",
      concept:"En ventas, el SI() es clave para automatizar el cálculo de comisiones y bonos sin hacer el cálculo manualmente cada mes.",
      xp:20, check: c => { const v=c["D15"]?.trim().toLowerCase().replace(/\s/g,""); return v.includes("si(b15>=c15")||v.includes("if(b15>=c15"); } },
    { id:"s6", phase:3, phaseLabel:"SI con comparación — Bonos",
      label:"Bonos para Carmen, Miguel y Daniela",
      description:"Aplica la misma lógica de bono en D16, D17 y D18",
      hint:"=SI(B16>=C16,...) para Carmen, misma lógica para las siguientes",
      concept:"Con una sola fórmula SI replicada, Excel calcula automáticamente quién recibe bono en una empresa con cientos de vendedores.",
      xp:20, check: c => {
        const ok = (id, r) => { const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v.includes(`si(b${r}>=c${r}`)||v.includes(`if(b${r}>=c${r}`); };
        return ok("D16",16)&&ok("D17",17)&&ok("D18",18);
      } },
  ],
};


// ═══════════════════════════════════════════════════════════════════════════════
//  MOTOR DE FÓRMULAS
// ═══════════════════════════════════════════════════════════════════════════════
const COLS_ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function parseRange(range, cells, evalFn) {
  const m = range.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/);
  if (!m) return [];
  const [,c1,r1,c2,r2] = m;
  const vals = [];
  for (let ci=COLS_ALPHA.indexOf(c1); ci<=COLS_ALPHA.indexOf(c2); ci++) {
    for (let ri=parseInt(r1); ri<=parseInt(r2); ri++) {
      const id = `${COLS_ALPHA[ci]}${ri}`;
      const v = evalFn(cells[id], cells);
      const n = parseFloat(v);
      if (!isNaN(n)) vals.push(n);
    }
  }
  return vals;
}

function parseRangeBounds(range) {
  const m = range.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/);
  if (!m) return null;
  const [,c1,r1,,r2] = m;
  return { c1, r1: parseInt(r1), r2: parseInt(r2) };
}

function getArgsRaw(raw) {
  const open = raw.indexOf("(");
  const close = raw.lastIndexOf(")");
  if (open === -1 || close === -1) return [];
  const inner = raw.slice(open + 1, close);
  const args = [];
  let cur = "", depth = 0, inQuotes = false;
  for (const ch of inner) {
    if (ch === '"') inQuotes = !inQuotes;
    if (!inQuotes) { if (ch === "(") depth++; if (ch === ")") depth--; }
    if (ch === "," && depth === 0 && !inQuotes) { args.push(cur); cur = ""; }
    else cur += ch;
  }
  args.push(cur);
  return args.map((a) => a.trim());
}

function resolveArg(argRaw, cells) {
  const a = (argRaw || "").trim();
  if (a.startsWith('"') && a.endsWith('"')) return a.slice(1, -1);
  if (/^-?\d+(\.\d+)?$/.test(a)) return a;
  if (/^\$?[A-Z]+\$?\d+$/i.test(a)) { const ref = a.replace(/\$/g, "").toUpperCase(); return evaluateCell(cells[ref], cells); }
  if (/^[A-Z.]+\(/i.test(a)) return evaluateCell(`=${a}`, cells);
  return a;
}

function matchesCriteria(value, criterio) {
  const c = String(criterio).trim();
  const m = c.match(/^(>=|<=|<>|>|<|=)?(.+)$/);
  const op = m[1] || "=";
  const rhsRaw = (m[2] || "").trim();
  const rhsNum = parseFloat(rhsRaw);
  const valNum = parseFloat(value);
  if (!isNaN(rhsNum) && !isNaN(valNum)) {
    switch (op) {
      case ">": return valNum > rhsNum;
      case "<": return valNum < rhsNum;
      case ">=": return valNum >= rhsNum;
      case "<=": return valNum <= rhsNum;
      case "<>": return valNum !== rhsNum;
      default: return valNum === rhsNum;
    }
  }
  return String(value).trim().toLowerCase() === rhsRaw.toLowerCase();
}

function evaluateCell(value, cells) {
  if (!value) return "";
  const raw = value.trim();
  if (!raw.startsWith("=")) return raw;
  const expr = raw.slice(1).toUpperCase().replace(/\s/g,"").replace(/\$/g,"");

  // SUMA / SUM
  const sumM = expr.match(/^S(?:UMA|UM)\(([A-Z]+\d+:[A-Z]+\d+)\)$/);
  if (sumM) { const v=parseRange(sumM[1],cells,evaluateCell); return String(v.reduce((a,b)=>a+b,0)); }

  // PROMEDIO / AVERAGE
  const avgM = expr.match(/^(?:PROMEDIO|AVERAGE)\(([A-Z]+\d+:[A-Z]+\d+)\)$/);
  if (avgM) { const v=parseRange(avgM[1],cells,evaluateCell); return v.length?String(v.reduce((a,b)=>a+b,0)/v.length):"0"; }

  // MAX
  const maxM = expr.match(/^MAX\(([A-Z]+\d+:[A-Z]+\d+)\)$/);
  if (maxM) { const v=parseRange(maxM[1],cells,evaluateCell); return v.length?String(Math.max(...v)):"0"; }

  // MIN
  const minM = expr.match(/^MIN\(([A-Z]+\d+:[A-Z]+\d+)\)$/);
  if (minM) { const v=parseRange(minM[1],cells,evaluateCell); return v.length?String(Math.min(...v)):"0"; }

  // CONTAR / COUNT
  const cntM = expr.match(/^(?:CONTAR|COUNT)\(([A-Z]+\d+:[A-Z]+\d+)\)$/);
  if (cntM) { return String(parseRange(cntM[1],cells,evaluateCell).length); }

  // BUSCARV / VLOOKUP
  if (expr.startsWith("BUSCARV(") || expr.startsWith("VLOOKUP(")) {
    try {
      const args = getArgsRaw(raw);
      const buscado = String(resolveArg(args[0], cells)).trim().toLowerCase();
      const bounds = parseRangeBounds(args[1].trim().toUpperCase());
      if (!bounds) return "#ERR";
      const colIdx = parseInt(resolveArg(args[2], cells));
      const targetCol = COLS_ALPHA[COLS_ALPHA.indexOf(bounds.c1) + colIdx - 1];
      for (let r = bounds.r1; r <= bounds.r2; r++) {
        const v = evaluateCell(cells[`${bounds.c1}${r}`], cells);
        if (String(v).trim().toLowerCase() === buscado) return evaluateCell(cells[`${targetCol}${r}`], cells);
      }
      return "#N/A";
    } catch { return "#ERR"; }
  }

  // SUMAR.SI / SUMIF
  if (expr.startsWith("SUMAR.SI(") || expr.startsWith("SUMIF(")) {
    try {
      const args = getArgsRaw(raw);
      const bounds = parseRangeBounds(args[0].trim().toUpperCase());
      if (!bounds) return "#ERR";
      const criterio = resolveArg(args[1], cells);
      const sumBounds = args[2] ? parseRangeBounds(args[2].trim().toUpperCase()) : bounds;
      let total = 0;
      for (let r = bounds.r1; r <= bounds.r2; r++) {
        const v = evaluateCell(cells[`${bounds.c1}${r}`], cells);
        if (matchesCriteria(v, criterio)) { const n = parseFloat(evaluateCell(cells[`${sumBounds.c1}${r}`], cells)); if (!isNaN(n)) total += n; }
      }
      return String(total);
    } catch { return "#ERR"; }
  }

  // CONTAR.SI / COUNTIF
  if (expr.startsWith("CONTAR.SI(") || expr.startsWith("COUNTIF(")) {
    try {
      const args = getArgsRaw(raw);
      const bounds = parseRangeBounds(args[0].trim().toUpperCase());
      if (!bounds) return "#ERR";
      const criterio = resolveArg(args[1], cells);
      let count = 0;
      for (let r = bounds.r1; r <= bounds.r2; r++) {
        const v = evaluateCell(cells[`${bounds.c1}${r}`], cells);
        if (matchesCriteria(v, criterio)) count++;
      }
      return String(count);
    } catch { return "#ERR"; }
  }

  // CONCATENAR / CONCAT
  if (expr.startsWith("CONCATENAR(") || expr.startsWith("CONCAT(")) {
    try { return getArgsRaw(raw).map((a) => resolveArg(a, cells)).join(""); } catch { return "#ERR"; }
  }

  // MAYUSC / UPPER
  if (expr.startsWith("MAYUSC(") || expr.startsWith("UPPER(")) {
    try { return String(resolveArg(getArgsRaw(raw)[0], cells)).toUpperCase(); } catch { return "#ERR"; }
  }

  // MINUSC / LOWER
  if (expr.startsWith("MINUSC(") || expr.startsWith("LOWER(")) {
    try { return String(resolveArg(getArgsRaw(raw)[0], cells)).toLowerCase(); } catch { return "#ERR"; }
  }

  // IZQUIERDA / LEFT
  if (expr.startsWith("IZQUIERDA(") || expr.startsWith("LEFT(")) {
    try { const args = getArgsRaw(raw); const s = String(resolveArg(args[0], cells)); const n = parseInt(resolveArg(args[1], cells)) || 1; return s.slice(0, n); } catch { return "#ERR"; }
  }

  // DERECHA / RIGHT
  if (expr.startsWith("DERECHA(") || expr.startsWith("RIGHT(")) {
    try { const args = getArgsRaw(raw); const s = String(resolveArg(args[0], cells)); const n = parseInt(resolveArg(args[1], cells)) || 1; return s.slice(-n); } catch { return "#ERR"; }
  }

  // LARGO / LEN
  if (expr.startsWith("LARGO(") || expr.startsWith("LEN(")) {
    try { return String(String(resolveArg(getArgsRaw(raw)[0], cells)).length); } catch { return "#ERR"; }
  }

  // EXTRAE / MID
  if (expr.startsWith("EXTRAE(") || expr.startsWith("MID(")) {
    try {
      const args = getArgsRaw(raw);
      const s = String(resolveArg(args[0], cells));
      const start = parseInt(resolveArg(args[1], cells), 10);
      const len = parseInt(resolveArg(args[2], cells), 10);
      return s.slice(start - 1, start - 1 + len);
    } catch { return "#ERR"; }
  }

  // SUSTITUIR / SUBSTITUTE (reemplaza todas las coincidencias)
  if (expr.startsWith("SUSTITUIR(") || expr.startsWith("SUBSTITUTE(")) {
    try {
      const args = getArgsRaw(raw);
      const s = String(resolveArg(args[0], cells));
      const oldText = String(resolveArg(args[1], cells));
      const newText = String(resolveArg(args[2], cells));
      return oldText ? s.split(oldText).join(newText) : s;
    } catch { return "#ERR"; }
  }

  // HALLAR / SEARCH (posición de la primera coincidencia, sin distinguir mayúsculas/minúsculas)
  if (expr.startsWith("HALLAR(") || expr.startsWith("SEARCH(")) {
    try {
      const args = getArgsRaw(raw);
      const needle = String(resolveArg(args[0], cells)).toLowerCase();
      const hay = String(resolveArg(args[1], cells)).toLowerCase();
      const startAt = args[2] ? parseInt(resolveArg(args[2], cells), 10) : 1;
      const idx = hay.indexOf(needle, startAt - 1);
      return idx === -1 ? "#VALUE" : String(idx + 1);
    } catch { return "#ERR"; }
  }

  // COINCIDIR / MATCH (posición 1-indexada de un valor dentro de un rango vertical)
  if (expr.startsWith("COINCIDIR(") || expr.startsWith("MATCH(")) {
    try {
      const args = getArgsRaw(raw);
      const buscado = String(resolveArg(args[0], cells)).trim().toLowerCase();
      const bounds = parseRangeBounds(args[1].trim().toUpperCase());
      if (!bounds) return "#ERR";
      for (let r = bounds.r1; r <= bounds.r2; r++) {
        const v = evaluateCell(cells[`${bounds.c1}${r}`], cells);
        if (String(v).trim().toLowerCase() === buscado) return String(r - bounds.r1 + 1);
      }
      return "#N/A";
    } catch { return "#ERR"; }
  }

  // INDICE / INDEX (valor en la posición N de un rango vertical)
  if (expr.startsWith("INDICE(") || expr.startsWith("INDEX(")) {
    try {
      const args = getArgsRaw(raw);
      const bounds = parseRangeBounds(args[0].trim().toUpperCase());
      if (!bounds) return "#ERR";
      const idx = parseInt(resolveArg(args[1], cells), 10);
      const targetRow = bounds.r1 + idx - 1;
      if (idx < 1 || targetRow > bounds.r2) return "#REF";
      return evaluateCell(cells[`${bounds.c1}${targetRow}`], cells);
    } catch { return "#ERR"; }
  }

  // Multiplicación A1*B1
  const mulM = expr.match(/^([A-Z]+\d+)\*([A-Z]+\d+)$/);
  if (mulM) { const a=parseFloat(evaluateCell(cells[mulM[1]],cells))||0; const b=parseFloat(evaluateCell(cells[mulM[2]],cells))||0; return String(a*b); }

  // Suma A1+B1
  const addM = expr.match(/^([A-Z]+\d+)\+([A-Z]+\d+)$/);
  if (addM) { const a=parseFloat(evaluateCell(cells[addM[1]],cells))||0; const b=parseFloat(evaluateCell(cells[addM[2]],cells))||0; return String(a+b); }

  // Resta A1-B1
  const subM = expr.match(/^([A-Z]+\d+)-([A-Z]+\d+)$/);
  if (subM) { const a=parseFloat(evaluateCell(cells[subM[1]],cells))||0; const b=parseFloat(evaluateCell(cells[subM[2]],cells))||0; return String(a-b); }

  // División A1/B1
  const divM = expr.match(/^([A-Z]+\d+)\/([A-Z]+\d+)$/);
  if (divM) { const a=parseFloat(evaluateCell(cells[divM[1]],cells))||0; const b=parseFloat(evaluateCell(cells[divM[2]],cells))||1; return String(a/b); }

  // SI.ERROR / IFERROR
  if (expr.startsWith("SI.ERROR(") || expr.startsWith("IFERROR(")) {
    try {
      const args = getArgsRaw(raw);
      const inner = args[0].trim();
      const result = evaluateCell(inner.startsWith("=") ? inner : `=${inner}`, cells);
      if (result === "#ERR" || result === "#N/A") return resolveArg(args[1], cells);
      return result;
    } catch { try { return resolveArg(getArgsRaw(raw)[1], cells); } catch { return "#ERR"; } }
  }

  // SI / IF (básico y anidado — evaluamos la fórmula cruda limpiando las comillas)
  if (expr.startsWith("SI(") || expr.startsWith("IF(")) {
    try {
      // Resolvemos referencias de celda dentro de la expresión primero
      const resolved = raw.slice(1).replace(/([A-Z]+\d+)/g, (ref) => {
        const v = evaluateCell(cells[ref.toUpperCase()], cells);
        const n = parseFloat(v);
        return !isNaN(n) ? n : `"${v}"`;
      });
      // Reemplazamos SI→ función JS, Y→&&, O→||, y el = de igualdad (Excel) por == de JS
      // (sin tocar >=, <= ni ==, que ya son válidos)
      const jsExpr = resolved
        .replace(/\bSI\b/gi, "_SI").replace(/\bIF\b/gi, "_SI")
        .replace(/\bY\b/gi, "_Y").replace(/\bO\b/gi, "_O")
        .replace(/(?<![<>=!])=(?!=)/g, "==");
      const _SI = (cond, t, f) => cond ? t : f;
      const _Y  = (...args) => args.every(Boolean);
      const _O  = (...args) => args.some(Boolean);
      // eslint-disable-next-line no-new-func
      const result = new Function("_SI","_Y","_O", `return ${jsExpr};`)(_SI, _Y, _O);
      return result !== undefined ? String(result) : "#ERR";
    } catch { return "#ERR"; }
  }

  return "#ERR";
}

function fmtNumber(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return val;
  const hasDecimal = val.includes(".");
  return n.toLocaleString("es-MX", {
    minimumFractionDigits: hasDecimal ? Math.min(2, (val.split(".")[1]||"").length) : 0,
    maximumFractionDigits: 2,
  });
}


// Replica una fórmula a otra fila (fill-down): incrementa referencias relativas, respeta $ absolutos
function shiftFormulaRefs(raw, rowOffset) {
  if (!raw || !raw.trim().startsWith("=")) return raw;
  let result = "", inQuotes = false, i = 0;
  while (i < raw.length) {
    const ch = raw[i];
    if (ch === '"') { inQuotes = !inQuotes; result += ch; i++; continue; }
    if (!inQuotes) {
      const m = raw.slice(i).match(/^(\$?)([A-Za-z]+)(\$?)(\d+)/);
      if (m) {
        const [full, dCol, col, dRow, rowNum] = m;
        const newRow = dRow ? rowNum : String(parseInt(rowNum, 10) + rowOffset);
        result += `${dCol}${col}${dRow}${newRow}`;
        i += full.length;
        continue;
      }
    }
    result += ch; i++;
  }
  return result;
}

// Controla el ciclo mount → visible → closing → unmount para animar entrada/salida de modales
function useMountTransition(isVisible, duration = 260) {
  const [shouldRender, setShouldRender] = useState(isVisible);
  const [closing, setClosing] = useState(false);
  useEffect(() => {
    let t;
    if (isVisible) { setShouldRender(true); setClosing(false); }
    else if (shouldRender) { setClosing(true); t = setTimeout(() => { setShouldRender(false); setClosing(false); }, duration); }
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, duration]);
  return { shouldRender, closing };
}

const KEYFRAMES_CSS = `
@keyframes xexBackdropIn { from{opacity:0} to{opacity:1} }
@keyframes xexBackdropOut { from{opacity:1} to{opacity:0} }
@keyframes xexModalIn { from{opacity:0;transform:scale(.85) translateY(18px)} to{opacity:1;transform:scale(1) translateY(0)} }
@keyframes xexModalOut { from{opacity:1;transform:scale(1) translateY(0)} to{opacity:0;transform:scale(.9) translateY(10px)} }
@keyframes xexObjPop { 0%{transform:scale(1)} 30%{transform:scale(1.045)} 60%{transform:scale(.99)} 100%{transform:scale(1)} }
@keyframes xexGlow { 0%{box-shadow:0 0 0 0 rgba(251,191,36,.55)} 70%{box-shadow:0 0 0 10px rgba(251,191,36,0)} 100%{box-shadow:0 0 0 0 rgba(251,191,36,0)} }
@keyframes xexXpFloat { 0%{opacity:0;transform:translateY(4px) scale(.8)} 18%{opacity:1;transform:translateY(0) scale(1.08)} 75%{opacity:1;transform:translateY(-16px) scale(1)} 100%{opacity:0;transform:translateY(-28px) scale(.95)} }
@keyframes xexBannerIn { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
@keyframes xexBannerOut { from{opacity:1;transform:translateY(0)} to{opacity:0;transform:translateY(-8px)} }
@keyframes xexCellFlash { 0%{background-color:#86efac} 100%{background-color:transparent} }
@keyframes xexRootExit { from{opacity:1;transform:scale(1)} to{opacity:0;transform:scale(.96)} }
`;


// ═══════════════════════════════════════════════════════════════════════════════
//  COMPONENTES UI
// ═══════════════════════════════════════════════════════════════════════════════
function MiniBarChart({ config, cells, evalFn }) {
  if (!config?.enabled || !config.bars?.length) return null;
  const vals = config.bars.map(b => parseFloat(evalFn(cells[b.cellId], cells)) || 0);
  const max = Math.max(...vals, 1);
  const W=228, H=88, PAD=20, GAP=6;
  const bW = (W - PAD*2 - GAP*(config.bars.length-1)) / config.bars.length;
  const hasData = vals.some(v=>v>0);

  return (
    <div style={{ padding:"10px 14px 4px", borderTop:"1px solid #e2e8f0", flexShrink:0 }}>
      <div style={{ fontSize:9.5, fontWeight:700, color:"#64748b", letterSpacing:1, textTransform:"uppercase", marginBottom:5 }}>
        {config.title}
      </div>
      {!hasData ? (
        <div style={{ fontSize:11, color:"#94a3b8", fontStyle:"italic", padding:"6px 0 8px" }}>
          {config.emptyMsg || "Completa las fórmulas para ver la gráfica"}
        </div>
      ) : (
        <svg width={W} height={H+18} style={{ display:"block" }}>
          {[0,.5,1].map(t=>(
            <line key={t} x1={PAD} y1={H-t*(H-8)} x2={W-PAD} y2={H-t*(H-8)} stroke="#e2e8f0" strokeWidth={1} strokeDasharray="3,3"/>
          ))}
          {config.bars.map((b,i)=>{
            const x = PAD + i*(bW+GAP);
            const bh = vals[i]>0 ? Math.max(4,(vals[i]/max)*(H-10)) : 0;
            return (
              <g key={b.cellId}>
                <rect x={x} y={H-bh} width={bW} height={bh} fill={b.color} rx={3} opacity={0.88}/>
                <text x={x+bW/2} y={H+13} textAnchor="middle" fontSize={8.5} fill="#64748b" fontFamily="sans-serif">{b.label}</text>
                {vals[i]>0 && <text x={x+bW/2} y={H-bh-3} textAnchor="middle" fontSize={8} fill={b.color} fontWeight="700" fontFamily="sans-serif">{config.format ? config.format(vals[i]) : vals[i]}</text>}
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

function ConceptBadge({ text }) {
  return (
    <div style={{ background:"linear-gradient(135deg,#1d6f42,#059669)", borderRadius:7, padding:"8px 10px", margin:"7px 0 3px", fontSize:12.5, color:"white", lineHeight:1.55, display:"flex", gap:6, alignItems:"flex-start" }}>
      <span style={{flexShrink:0}}>💡</span><span>{text}</span>
    </div>
  );
}

const PHASE_COLORS = [
  { bg:"#dbeafe", text:"#1e40af" },
  { bg:"#fef3c7", text:"#92400e" },
  { bg:"#f3e8ff", text:"#6b21a8" },
  { bg:"#dcfce7", text:"#166534" },
  { bg:"#ffe4e6", text:"#9f1239" },
];

function headerTH(active=false, hover=false) {
  return {
    background: active ? "#c6e4d2" : hover ? "#eef5ee" : "#e8f0e8",
    border:"1px solid #d1d5db", padding:"2px 5px",
    fontSize:11, fontWeight:active?700:600, textAlign:"center",
    userSelect:"none", color:active?"#1d6f42":"#475569",
    position:"sticky", top:0, zIndex:2, minWidth:28,
    transition:"background .12s",
  };
}

function ribbonBtnStyle(active, enabled) {
  return {
    width:26, height:24, border:`1px solid ${active?"#1d6f42":"#d1d5db"}`,
    background:active?"#dcfce7":"#fff", color: !enabled ? "#cbd5e1" : (active?"#1d6f42":"#334155"),
    borderRadius:4, fontSize:12, fontWeight:700, cursor:enabled?"pointer":"not-allowed",
    display:"flex", alignItems:"center", justifyContent:"center", padding:0,
  };
}

function alignBtnStyle(active, enabled) {
  return {
    padding:"0 9px", height:24, border:`1px solid ${active?"#1d6f42":"#d1d5db"}`,
    background:active?"#dcfce7":"#fff", color: !enabled ? "#cbd5e1" : (active?"#1d6f42":"#334155"),
    borderRadius:4, fontSize:10.5, fontWeight:600, cursor:enabled?"pointer":"not-allowed",
  };
}


// ═══════════════════════════════════════════════════════════════════════════════
//  COMPONENTE PRINCIPAL — ExcelExerciseTemplate
// ═══════════════════════════════════════════════════════════════════════════════
function ExcelExerciseTemplate({ config = EJERCICIO_REFERENCIAS, onExit, onComplete }) {
  const GRID_COLS = config.cols  || ["A","B","C","D","E","F"];
  const GRID_ROWS = Array.from({ length: config.rows || 18 }, (_,i) => i+1);
  const LOCKED     = new Set(config.lockedCells || []);

  const [cells,          setCells]          = useState(config.initialData || {});
  const [activeCell,     setActiveCell]     = useState(null);
  const [editValue,      setEditValue]      = useState("");
  const [isEditing,      setIsEditing]      = useState(false);
  const [completedObjs,  setCompletedObjs]  = useState({});
  const [newlyCompleted, setNewlyCompleted] = useState(null);
  const [activeHint,     setActiveHint]     = useState(null);
  const [expandedConcept,setExpandedConcept]= useState(null);
  const [showIntro,      setShowIntro]      = useState(true);
  const [showCelebration,setShowCelebration]= useState(false);
  const [colWidthsState, setColWidthsState] = useState(() => ({ ...(config.colWidths || {}) }));
  const [cellFormats,    setCellFormats]    = useState({});
  const [hoverCell,      setHoverCell]      = useState(null);
  const [flashCell,      setFlashCell]      = useState(null);
  const [objectiveBanner,setObjectiveBanner]= useState(null);
  const [ribbonTab,      setRibbonTab]      = useState("Inicio");
  const [fillDrag,       setFillDrag]       = useState(null);
  const [tourRun,        setTourRun]        = useState(false);
  const [isExiting,      setIsExiting]      = useState(false);
  const inputRef = useRef(null);
  const lastEditedCellRef = useRef(null);
  const resizeRef = useRef(null);
  const cellRefs = useRef({});
  const historyRef = useRef([]);
  const redoRef = useRef([]);
  const clipboardRef = useRef(null);

  const handleExit = () => {
    if (!onExit || isExiting) return;
    setIsExiting(true);
    setTimeout(() => onExit(), 320);
  };

  // Reset on config change
  useEffect(() => {
    setCells(config.initialData || {});
    setActiveCell(null); setEditValue(""); setIsEditing(false);
    setCompletedObjs({}); setNewlyCompleted(null);
    setActiveHint(null); setExpandedConcept(null);
    setShowIntro(true); setShowCelebration(false);
    setColWidthsState({ ...(config.colWidths || {}) });
    setCellFormats({}); setHoverCell(null); setFlashCell(null);
    setObjectiveBanner(null); setRibbonTab("Inicio"); setFillDrag(null);
    setTourRun(false); setIsExiting(false);
    historyRef.current = []; redoRef.current = []; clipboardRef.current = null;
  }, [config]);

  // Check objectives
  useEffect(() => {
    const next = {};
    config.objectives.forEach(o => { next[o.id] = o.check(cells); });
    const justDone = config.objectives.find(o => next[o.id] && !completedObjs[o.id]);
    if (justDone) {
      setNewlyCompleted(justDone.id);
      setExpandedConcept(justDone.id);
      setObjectiveBanner({ label: justDone.label, xp: justDone.xp });
      if (lastEditedCellRef.current) {
        setFlashCell(lastEditedCellRef.current);
        setTimeout(() => setFlashCell(null), 900);
      }
      setTimeout(() => setNewlyCompleted(null), 2500);
      setTimeout(() => setObjectiveBanner(null), 2300);
    }
    const prevAll = config.objectives.every(o => completedObjs[o.id]);
    const nowAll  = config.objectives.every(o => next[o.id]);
    if (nowAll && !prevAll && Object.keys(completedObjs).length > 0) {
      setShowCelebration(true);
      if (onComplete) onComplete();
    }
    setCompletedObjs(next);
  }, [cells]);

  // Arrastre del fill handle (copiar fórmula hacia abajo, ajustando referencias relativas)
  useEffect(() => {
    if (!fillDrag) return;
    const onMove = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const rowAttr = el?.closest?.("[data-cell-row]")?.getAttribute("data-cell-row");
      if (!rowAttr) return;
      const newEndRow = parseInt(rowAttr, 10);
      setFillDrag(fd => {
        if (!fd) return fd;
        const clamped = Math.max(fd.startRow, newEndRow);
        return clamped === fd.endRow ? fd : { ...fd, endRow: clamped };
      });
    };
    const onUp = () => {
      setFillDrag(fd => {
        if (fd && fd.endRow > fd.startRow) {
          const sourceId = `${fd.col}${fd.startRow}`;
          const sourceVal = cells[sourceId];
          if (sourceVal) {
            setCells(prev => {
              historyRef.current.push(prev);
              if (historyRef.current.length > 100) historyRef.current.shift();
              redoRef.current = [];
              const next = { ...prev };
              for (let r = fd.startRow + 1; r <= fd.endRow; r++) {
                const targetId = `${fd.col}${r}`;
                if (!LOCKED.has(targetId)) next[targetId] = shiftFormulaRefs(sourceVal, r - fd.startRow);
              }
              return next;
            });
          }
        }
        return null;
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fillDrag]);

  // Mueve el foco DOM real a la celda activa (necesario para que las teclas de flecha
  // sigan navegando: cada <td> maneja keydown con su propio col/row fijo, así que si el
  // foco no se mueve, las flechas repetidas quedan calculando siempre desde la misma celda).
  useEffect(() => {
    if (activeCell && !isEditing) cellRefs.current[activeCell]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCell]);

  const totalXP  = config.objectives.reduce((s,o)=>s+o.xp,0);
  const earnedXP = config.objectives.reduce((s,o)=>s+(completedObjs[o.id]?o.xp:0),0);
  const progress = totalXP > 0 ? (earnedXP/totalXP)*100 : 0;
  const doneCount = Object.values(completedObjs).filter(Boolean).length;

  // Cell interaction
  const selectCell = useCallback((col, row) => {
    const id = `${col}${row}`;
    setActiveCell(id); setEditValue(cells[id]??""); setIsEditing(false);
  }, [cells]);

  const commitEdit = useCallback((id, value) => {
    if (LOCKED.has(id)) return;
    lastEditedCellRef.current = id;
    setCells(prev => {
      if (prev[id] === value) return prev;
      historyRef.current.push(prev);
      if (historyRef.current.length > 100) historyRef.current.shift();
      redoRef.current = [];
      return { ...prev, [id]: value };
    });
    setIsEditing(false);
  }, [LOCKED]);

  // Pega texto (una celda, o un bloque tipo Excel separado por tabs/saltos de línea)
  // a partir de la celda dada, respetando celdas bloqueadas y los límites del grid.
  const pasteIntoCells = useCallback((startCol, startRow, text) => {
    const lines = text.replace(/\r/g, "").replace(/\n$/, "").split("\n");
    const startCi = GRID_COLS.indexOf(startCol);
    const maxRow = GRID_ROWS[GRID_ROWS.length - 1];
    let lastId = null;
    setCells(prev => {
      historyRef.current.push(prev);
      if (historyRef.current.length > 100) historyRef.current.shift();
      redoRef.current = [];
      const next = { ...prev };
      lines.forEach((line, ri) => {
        const values = line.split("\t");
        values.forEach((val, ci) => {
          const targetCol = GRID_COLS[startCi + ci];
          const targetRow = startRow + ri;
          if (!targetCol || targetRow > maxRow) return;
          const targetId = `${targetCol}${targetRow}`;
          if (LOCKED.has(targetId)) return;
          next[targetId] = val;
          lastId = targetId;
        });
      });
      return next;
    });
    if (lastId) lastEditedCellRef.current = lastId;
  }, [GRID_COLS, GRID_ROWS, LOCKED]);

  const undo = useCallback(() => {
    setCells(curr => {
      if (historyRef.current.length === 0) return curr;
      const prevSnapshot = historyRef.current.pop();
      redoRef.current.push(curr);
      if (redoRef.current.length > 100) redoRef.current.shift();
      return prevSnapshot;
    });
  }, []);

  const redo = useCallback(() => {
    setCells(curr => {
      if (redoRef.current.length === 0) return curr;
      const nextSnapshot = redoRef.current.pop();
      historyRef.current.push(curr);
      if (historyRef.current.length > 100) historyRef.current.shift();
      return nextSnapshot;
    });
  }, []);

  const startColResize = useCallback((e, col) => {
    e.stopPropagation(); e.preventDefault();
    resizeRef.current = { col, startX: e.clientX, startWidth: colWidthsState[col] || 100 };
    const onMove = (ev) => {
      if (!resizeRef.current) return;
      const { col: c, startX, startWidth } = resizeRef.current;
      const newW = Math.max(50, startWidth + (ev.clientX - startX));
      setColWidthsState(prev => (prev[c] === newW ? prev : { ...prev, [c]: newW }));
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [colWidthsState]);

  const startFillDrag = useCallback((e) => {
    e.stopPropagation(); e.preventDefault();
    if (!activeCell) return;
    const col = activeCell[0]; const row = parseInt(activeCell.slice(1), 10);
    setFillDrag({ col, startRow: row, endRow: row });
  }, [activeCell]);

  const canFormat = !!activeCell && !LOCKED.has(activeCell);
  const activeFormat = activeCell ? (cellFormats[activeCell] || {}) : {};

  const toggleFormat = (key) => {
    if (!canFormat) return;
    setCellFormats(prev => ({ ...prev, [activeCell]: { ...prev[activeCell], [key]: !prev[activeCell]?.[key] } }));
  };

  const setAlign = (align) => {
    if (!canFormat) return;
    setCellFormats(prev => ({ ...prev, [activeCell]: { ...prev[activeCell], align } }));
  };

  const handleKeyDown = (e, col, row) => {
    const id = `${col}${row}`;
    const ci = GRID_COLS.indexOf(col);
    const maxRow = GRID_ROWS[GRID_ROWS.length-1];
    const isMod = e.ctrlKey || e.metaKey;
    const key = e.key.toLowerCase();
    // Ctrl/Cmd+Z y Ctrl/Cmd+Y (o +Shift+Z) — solo fuera de edición: al escribir, el
    // undo nativo del <input> debe encargarse de deshacer el texto que se está tecleando.
    if (!isEditing && isMod && key==="z" && !e.shiftKey) { e.preventDefault(); undo(); return; }
    if (!isEditing && isMod && (key==="y" || (key==="z" && e.shiftKey))) { e.preventDefault(); redo(); return; }
    // Copy/paste/cut se resuelven vía los eventos nativos onCopy/onPaste (necesitan
    // clipboardData). Aquí solo evitamos que "c"/"v"/"x" con Ctrl se traten como texto.
    if (isMod && ["c","v","x","a"].includes(key)) return;
    if (e.key==="Enter")  { if(isEditing) commitEdit(id,editValue); selectCell(col,Math.min(row+1,maxRow)); e.preventDefault(); }
    else if (e.key==="Tab")    { if(isEditing) commitEdit(id,editValue); selectCell(GRID_COLS[Math.min(ci+1,GRID_COLS.length-1)],row); e.preventDefault(); }
    else if (e.key==="Escape") { setEditValue(cells[id]??""); setIsEditing(false); }
    else if (!isEditing && e.key==="ArrowDown")  { selectCell(col,Math.min(row+1,maxRow)); e.preventDefault(); }
    else if (!isEditing && e.key==="ArrowUp")    { selectCell(col,Math.max(row-1,1)); e.preventDefault(); }
    else if (!isEditing && e.key==="ArrowRight") { selectCell(GRID_COLS[Math.min(ci+1,GRID_COLS.length-1)],row); e.preventDefault(); }
    else if (!isEditing && e.key==="ArrowLeft")  { selectCell(GRID_COLS[Math.max(ci-1,0)],row); e.preventDefault(); }
    else if (!isEditing && e.key==="Delete" && !LOCKED.has(id)) commitEdit(id,"");
    else if (!isEditing && e.key==="Backspace" && !LOCKED.has(id)) { commitEdit(id,""); setEditValue(""); setIsEditing(true); e.preventDefault(); }
    else if (!isEditing && !isMod && e.key.length===1 && !LOCKED.has(id)) { setEditValue(e.key); setIsEditing(true); }
  };

  const displayVal = (id) => {
    const raw = cells[id];
    if (!raw) return "";
    if (raw.trim().startsWith("=")) {
      const ev = evaluateCell(raw, cells);
      if (ev==="#ERR") return "#ERR";
      const n = parseFloat(ev);
      return !isNaN(n) ? fmtNumber(ev) : ev;
    }
    const n = parseFloat(raw);
    return !isNaN(n) && !isNaN(Number(raw)) ? fmtNumber(raw) : raw;
  };

  const getCellVisualStyle = (id) => {
    if (config.getCellStyle) {
      const s = config.getCellStyle(id, cells);
      if (s) return s;
    }
    return null;
  };

  const phases = [...new Set(config.objectives.map(o=>o.phase))];
  const introTransition = useMountTransition(showIntro, 260);
  const celebrationTransition = useMountTransition(showCelebration, 260);
  const bannerTransition = useMountTransition(!!objectiveBanner, 240);

  return (
    <div className={"exc-container"}  style={{
      fontFamily:"'Segoe UI',system-ui,sans-serif", background:"#f0f4f8", height:"100vh", overflow:"hidden", display:"flex", flexDirection:"column", color:"#1e293b",
      animation: isExiting ? "xexRootExit .32s ease forwards" : "none",
    }}>
      <style>{KEYFRAMES_CSS}</style>

      <Joyride
        locale={spanishOptionsJoyRide}
        steps={EXCEL_TOUR_STEPS}
        run={tourRun}
        continuous
        showSkipButton
        hideCloseButton
        disableScrolling
        callback={(data) => {
          if (data.status === "finished" || data.status === "skipped") setTourRun(false);
        }}
        styles={{ options: { primaryColor: "#1d6f42", textColor: "#1e293b", zIndex: 1000 } }}
      />

      {/* ── TOPBAR ── */}
      <div style={{ background:"#1d6f42", height:50, display:"flex", alignItems:"center", padding:"0 16px", gap:12, boxShadow:"0 2px 10px rgba(0,0,0,.25)", flexShrink:0 }}>
        <div style={{ background:"white", color:"#1d6f42", fontWeight:900, fontSize:17, padding:"2px 7px", borderRadius:4, letterSpacing:"-1px" }}>X</div>
        <div>
          <div style={{ color:"white", fontWeight:700, fontSize:13.5 }}>{config.title}</div>
          <div style={{ color:"rgba(255,255,255,.6)", fontSize:10.5 }}>{config.subtitle}</div>
        </div>
        <div style={{ marginLeft:"auto", display:"flex", gap:10, alignItems:"center" }}>
          <button
            onClick={() => setTourRun(true)}
            title="Ver tutorial guiado"
            style={{ background:"rgba(255,255,255,.15)", border:"none", borderRadius:"50%", width:24, height:24, color:"white", fontSize:12, fontWeight:800, cursor:"pointer" }}
          >
            ?
          </button>
          <div className="xex-topbar-xp" style={{ background:"rgba(255,255,255,.15)", borderRadius:20, padding:"3px 13px", color:"white", fontSize:12, fontWeight:700 }}>
            ⭐ {earnedXP}/{totalXP} XP
          </div>
          <div style={{ position:"relative", width:32, height:32 }}>
            <svg width={32} height={32} style={{ transform:"rotate(-90deg)" }}>
              <circle cx={16} cy={16} r={13} fill="none" stroke="rgba(255,255,255,.2)" strokeWidth={3}/>
              <circle cx={16} cy={16} r={13} fill="none" stroke="#86efac" strokeWidth={3}
                strokeDasharray={`${2*Math.PI*13}`} strokeDashoffset={`${2*Math.PI*13*(1-progress/100)}`}
                strokeLinecap="round" style={{ transition:"stroke-dashoffset .5s" }}/>
            </svg>
            <div style={{ position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8.5,fontWeight:800,color:"white" }}>
              {Math.round(progress)}%
            </div>
          </div>
          {onExit && (
            <button
              onClick={handleExit}
              title="Salir del ejercicio"
              style={{ background:"rgba(0,0,0,.18)", border:"1px solid rgba(255,255,255,.25)", borderRadius:6, padding:"5px 11px", color:"white", fontSize:11.5, fontWeight:700, cursor:"pointer" }}
            >
              ✕ Salir
            </button>
          )}
        </div>
      </div>

      {/* ── RIBBON ── */}
      <div className="xex-ribbon" style={{ background:"#fff", borderBottom:"1px solid #d1d5db", flexShrink:0 }}>
        <div style={{ display:"flex", gap:2, padding:"3px 10px 0" }}>
          {["Inicio","Insertar","Diseño","Fórmulas","Datos","Vista"].map((t)=>(
            <button key={t} onClick={()=>setRibbonTab(t)}
              style={{ background:ribbonTab===t?"#e8f5ee":"transparent", border:"none", borderBottom:ribbonTab===t?"2px solid #1d6f42":"2px solid transparent", borderRadius:"4px 4px 0 0", padding:"4px 12px", fontSize:11, color:ribbonTab===t?"#1d6f42":"#64748b", fontWeight:ribbonTab===t?700:400, cursor:"pointer" }}>
              {t}
            </button>
          ))}
        </div>
        {ribbonTab === "Inicio" && (
          <div style={{ display:"flex", alignItems:"center", gap:12, padding:"6px 10px 8px" }}>
            <div style={{ display:"flex", gap:3 }}>
              <button title="Negrita" disabled={!canFormat} onClick={()=>toggleFormat("bold")} style={ribbonBtnStyle(activeFormat.bold, canFormat)}>B</button>
              <button title="Cursiva" disabled={!canFormat} onClick={()=>toggleFormat("italic")} style={{...ribbonBtnStyle(activeFormat.italic, canFormat), fontStyle:"italic"}}>I</button>
              <button title="Subrayado" disabled={!canFormat} onClick={()=>toggleFormat("underline")} style={{...ribbonBtnStyle(activeFormat.underline, canFormat), textDecoration:"underline"}}>U</button>
            </div>
            <div style={{ width:1, alignSelf:"stretch", background:"#e2e8f0" }}/>
            <div style={{ display:"flex", gap:3 }}>
              <button title="Alinear izquierda" disabled={!canFormat} onClick={()=>setAlign("left")} style={alignBtnStyle(activeFormat.align==="left", canFormat)}>Izq</button>
              <button title="Centrar" disabled={!canFormat} onClick={()=>setAlign("center")} style={alignBtnStyle(activeFormat.align==="center", canFormat)}>Centro</button>
              <button title="Alinear derecha" disabled={!canFormat} onClick={()=>setAlign("right")} style={alignBtnStyle(activeFormat.align==="right", canFormat)}>Der</button>
            </div>
            {!canFormat && <span style={{ fontSize:10.5, color:"#94a3b8", fontStyle:"italic" }}>Selecciona una celda editable para aplicar formato</span>}
          </div>
        )}
      </div>

      {/* ── FORMULA BAR ── */}
      <div className="xex-formula-bar" style={{ background:"#fff", borderBottom:"2px solid #e2e8f0", padding:"4px 10px", display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
        <div style={{ background:"#f1f5f9", border:"1px solid #cbd5e1", borderRadius:4, padding:"3px 8px", minWidth:46, textAlign:"center", fontSize:12, fontWeight:700, fontFamily:"monospace", color:"#1d6f42" }}>
          {activeCell||""}
        </div>
        <span style={{ color:"#94a3b8", fontSize:14, fontStyle:"italic" }}>fx</span>
        <input
          value={activeCell ? (isEditing ? editValue : (cells[activeCell]??"")) : ""}
          onChange={e => { if(activeCell && !LOCKED.has(activeCell)){ setEditValue(e.target.value); setIsEditing(true); }}}
          onKeyDown={e => { if(!activeCell) return; const col=activeCell[0]; const row=parseInt(activeCell.slice(1)); handleKeyDown(e,col,row); }}
          placeholder="Selecciona una celda para editar…"
          readOnly={activeCell ? LOCKED.has(activeCell) : false}
          style={{ flex:1, border:"1px solid #cbd5e1", borderRadius:4, padding:"3px 10px", fontSize:12, fontFamily:"monospace", outline:"none", background: activeCell&&LOCKED.has(activeCell)?"#f8fafc":"white" }}
        />
        {activeCell && LOCKED.has(activeCell) && (
          <span style={{ fontSize:10, color:"#94a3b8", whiteSpace:"nowrap" }}>🔒 Solo lectura</span>
        )}
      </div>

      {/* ── MAIN ── */}
      <div style={{ display:"flex", flex:1, overflow:"hidden" }}>

        {/* ── SPREADSHEET ── */}
        <div className="xex-grid" style={{ flex:1, overflow:"auto" }}>
          <table style={{ borderCollapse:"collapse", tableLayout:"fixed", background:"#fff", minWidth:"100%" }}>
            <colgroup>
              <col style={{ width:36 }}/>
              {GRID_COLS.map(c=><col key={c} style={{ width:colWidthsState[c]||100 }}/>)}
            </colgroup>
            <thead>
              <tr>
                <th style={headerTH()}></th>
                {GRID_COLS.map(c=>(
                  <th key={c} style={{ ...headerTH(activeCell?.startsWith(c), hoverCell?.startsWith(c)), position:"sticky" }}>
                    {c}
                    <div
                      onMouseDown={(e)=>startColResize(e,c)}
                      title="Arrastra para redimensionar"
                      style={{ position:"absolute", right:0, top:0, bottom:0, width:5, cursor:"col-resize", zIndex:3 }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {GRID_ROWS.map(row=>(
                <tr key={row}>
                  <td style={{ ...headerTH(activeCell?.endsWith(String(row)), hoverCell?.endsWith(String(row))), fontSize:10.5, textAlign:"center" }}>{row}</td>
                  {GRID_COLS.map(col=>{
                    const id=`${col}${row}`;
                    const isActive = activeCell===id;
                    const isLocked = LOCKED.has(id);
                    const cs = getCellVisualStyle(id);
                    const fmt = cellFormats[id] || {};
                    const dv = displayVal(id);
                    const isNum = !isNaN(parseFloat(dv?.replace(/,/g,""))) && col!=="A";
                    const isErr = dv==="#ERR";
                    const isFlashing = flashCell === id;
                    const inFillPreview = fillDrag && col===fillDrag.col && row>fillDrag.startRow && row<=fillDrag.endRow;

                    return (
                      <td key={id}
                        ref={(el) => { cellRefs.current[id] = el; }}
                        data-cell-row={row}
                        tabIndex={0}
                        onClick={() => selectCell(col,row)}
                        onDoubleClick={() => { if(!isLocked){ selectCell(col,row); setIsEditing(true); }}}
                        onKeyDown={(e) => { if (!(isActive && isEditing)) handleKeyDown(e, col, row); }}
                        onCopy={(e) => {
                          if (isEditing) return; // deja que el navegador copie el texto seleccionado del input
                          e.preventDefault();
                          const raw = cells[id] ?? "";
                          clipboardRef.current = raw;
                          e.clipboardData.setData("text/plain", raw);
                        }}
                        onCut={(e) => {
                          if (isEditing) return;
                          e.preventDefault();
                          const raw = cells[id] ?? "";
                          clipboardRef.current = raw;
                          e.clipboardData.setData("text/plain", raw);
                          if (!isLocked) commitEdit(id, "");
                        }}
                        onPaste={(e) => {
                          if (isEditing) return; // deja que el navegador pegue dentro del input
                          e.preventDefault();
                          const text = e.clipboardData.getData("text/plain") ?? clipboardRef.current ?? "";
                          if (text) pasteIntoCells(col, row, text);
                        }}
                        onMouseEnter={() => setHoverCell(id)}
                        onMouseLeave={() => setHoverCell(prev => prev===id ? null : prev)}
                        style={{
                          border: isActive ? "2px solid #1d6f42" : inFillPreview ? "1px dashed #1d6f42" : "1px solid #e2e8f0",
                          padding:0, position:"relative", cursor: isLocked?"default":"cell",
                          background: isActive ? "#f0fdf4" : inFillPreview ? "#f0fdf4" : (cs?.bg||"white"),
                          boxSizing:"border-box", outline:"none",
                          animation: isFlashing ? "xexCellFlash .9s ease-out" : "none",
                        }}
                      >
                        {isActive && isEditing && !isLocked ? (
                          <input ref={inputRef} autoFocus value={editValue}
                            onChange={e=>setEditValue(e.target.value)}
                            onBlur={()=>commitEdit(id,editValue)}
                            onKeyDown={e=>handleKeyDown(e,col,row)}
                            style={{ width:"100%", border:"none", outline:"none", padding:"2px 5px", fontFamily:"monospace", fontSize:11.5, background:"transparent", minHeight:24, boxSizing:"border-box" }}
                          />
                        ) : (
                          <div style={{
                            padding:"2px 5px", minHeight:24, lineHeight:"20px",
                            fontSize: cs?.fontSize||11.5,
                            fontFamily: isNum ? "monospace" : "'Segoe UI',sans-serif",
                            fontWeight: fmt.bold ? 700 : (cs?.fontWeight||400),
                            fontStyle:  (fmt.italic || cs?.italic) ? "italic":"normal",
                            textDecoration: fmt.underline ? "underline" : "none",
                            color: isActive?"#1d6f42":(isErr?"#ef4444":(cs?.color||"#1e293b")),
                            textAlign: fmt.align || (cs?.align) || (isNum?"right":"left"),
                            whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                          }}>
                            {dv}
                          </div>
                        )}
                        {isActive && !isLocked && !isEditing && (
                          <div
                            onMouseDown={startFillDrag}
                            title="Arrastra para copiar la fórmula hacia abajo"
                            style={{ position:"absolute", right:-3, bottom:-3, width:7, height:7, background:"#1d6f42", border:"1px solid white", borderRadius:1, cursor:"crosshair", zIndex:5 }}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── SIDEBAR ── */}
        <div className="xex-sidebar-objectives" style={{ width:296, background:"#fff", borderLeft:"2px solid #e2e8f0", display:"flex", flexDirection:"column", flexShrink:0, overflow:"hidden" }}>

          {/* Header */}
          <div style={{ background:"#1d6f42", padding:"11px 14px 9px", flexShrink:0 }}>
            <div style={{ color:"rgba(255,255,255,.65)", fontSize:9, letterSpacing:1.5, textTransform:"uppercase", marginBottom:2 }}>Objetivos</div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ color:"white", fontWeight:800, fontSize:14 }}>{doneCount}/{config.objectives.length} completados</span>
              <span style={{ color:"#86efac", fontWeight:700, fontSize:11 }}>+{earnedXP} XP</span>
            </div>
            <div style={{ marginTop:6, background:"rgba(255,255,255,.2)", borderRadius:99, height:5 }}>
              <div style={{ height:"100%", width:`${progress}%`, background:progress===100?"#fbbf24":"#86efac", borderRadius:99, transition:"width .5s" }}/>
            </div>
          </div>

          {/* Banner de objetivo recién completado */}
          {bannerTransition.shouldRender && objectiveBanner && (
            <div style={{
              background:"linear-gradient(135deg,#fbbf24,#f59e0b)", color:"#78350f", padding:"7px 14px",
              fontSize:11.5, fontWeight:700, display:"flex", alignItems:"center", gap:6, flexShrink:0,
              animation:`${bannerTransition.closing?"xexBannerOut":"xexBannerIn"} .24s ease forwards`,
            }}>
              <span>🎉</span>
              <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>¡Completaste "{objectiveBanner.label}"!</span>
              <span>+{objectiveBanner.xp} XP</span>
            </div>
          )}

          {/* Chart (if enabled) */}
          <MiniBarChart config={config.chart} cells={cells} evalFn={evaluateCell}/>

          {/* Objectives list */}
          <div style={{ flex:1, overflowY:"auto", padding:"8px 10px 10px" }}>
            {phases.map(phase=>{
              const phaseObjs = config.objectives.filter(o=>o.phase===phase);
              const ph = PHASE_COLORS[(phase-1) % PHASE_COLORS.length];
              const phaseDone = phaseObjs.every(o=>completedObjs[o.id]);
              const phaseLabel = phaseObjs[0]?.phaseLabel || `Fase ${phase}`;

              return (
                <div key={phase} style={{ marginBottom:8 }}>
                  <div style={{ background:phaseDone?"#dcfce7":ph.bg, color:phaseDone?"#166534":ph.text, fontWeight:700, fontSize:9.5, letterSpacing:.7, padding:"3px 9px", borderRadius:5, marginBottom:4, textTransform:"uppercase", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span>Fase {phase} — {phaseLabel}</span>
                    {phaseDone && <span>✅</span>}
                  </div>

                  {phaseObjs.map(obj=>{
                    const done = completedObjs[obj.id];
                    const isNew = newlyCompleted===obj.id;
                    const showConcept = expandedConcept===obj.id;

                    return (
                      <div key={obj.id} style={{
                        borderRadius:7, padding:"8px 10px", marginBottom:5, position:"relative",
                        background:done?"#f0fdf4":"#f8fafc",
                        border:`1.5px solid ${isNew?"#fbbf24":done?"#86efac":"#e2e8f0"}`,
                        transition:"background .3s, border-color .3s",
                        animation: isNew ? "xexObjPop .5s ease, xexGlow 1.3s ease" : "none",
                      }}>
                        {isNew && (
                          <span style={{
                            position:"absolute", top:-6, right:8, fontSize:10.5, fontWeight:800, color:"#b45309",
                            background:"white", padding:"1px 7px", borderRadius:99, boxShadow:"0 2px 6px rgba(0,0,0,.18)",
                            animation:"xexXpFloat 1.3s ease-out forwards", pointerEvents:"none", zIndex:2,
                          }}>
                            +{obj.xp} XP
                          </span>
                        )}
                        <div style={{ display:"flex", gap:7, alignItems:"flex-start" }}>
                          <span style={{ flexShrink:0, fontSize:done?14:13, marginTop:1, opacity:done?1:.4, transition:"opacity .3s, font-size .3s" }}>{done?"✅":"○"}</span>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:13, fontWeight:700, color:done?"#166534":"#1e293b", lineHeight:1.35, marginBottom:3 }}>{obj.label}</div>
                            <div style={{ fontSize:12.5, color:"#475569", lineHeight:1.55, marginBottom:6 }}>{obj.description}</div>

                            <div style={{ maxHeight: (done && showConcept) ? 220 : 0, opacity: (done && showConcept) ? 1 : 0, overflow:"hidden", transition:"max-height .28s ease, opacity .22s ease" }}>
                              <ConceptBadge text={obj.concept}/>
                            </div>

                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                              <span style={{ fontSize:10.5, fontWeight:700, color:done?"#22c55e":"#94a3b8" }}>
                                {done?"✓ ":""} +{obj.xp} XP
                              </span>
                              <div style={{ display:"flex", gap:4 }}>
                                {done && (
                                  <button onClick={()=>setExpandedConcept(showConcept?null:obj.id)}
                                    style={{ background:"none", border:"1px solid #86efac", borderRadius:4, padding:"1px 7px", fontSize:10, color:"#166534", cursor:"pointer", fontWeight:600, transition:"background .15s" }}>
                                    {showConcept?"▲":"📖 Concepto"}
                                  </button>
                                )}
                                {!done && (
                                  <button onClick={()=>setActiveHint(activeHint===obj.id?null:obj.id)}
                                    style={{ background:"none", border:"1px solid #cbd5e1", borderRadius:4, padding:"1px 7px", fontSize:10, color:"#64748b", cursor:"pointer", transition:"background .15s" }}>
                                    {activeHint===obj.id?"Ocultar":"💡 Pista"}
                                  </button>
                                )}
                              </div>
                            </div>

                            <div style={{ maxHeight: (activeHint===obj.id && !done) ? 160 : 0, opacity: (activeHint===obj.id && !done) ? 1 : 0, overflow:"hidden", transition:"max-height .25s ease, opacity .2s ease" }}>
                              <div style={{ marginTop:5, background:"#fffbeb", border:"1px solid #fde68a", borderRadius:5, padding:"6px 9px", fontSize:12.5, color:"#92400e", lineHeight:1.55 }}>
                                💡 {obj.hint}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ borderTop:"1px solid #e2e8f0", padding:"7px 14px", background:"#f8fafc", flexShrink:0, display:"flex", justifyContent:"space-between", fontSize:11, color:"#64748b" }}>
            <span>Avance total</span>
            <span style={{ fontWeight:800, color:progress===100?"#22c55e":"#1d6f42" }}>{Math.round(progress)}%</span>
          </div>
        </div>
      </div>

      {/* ── STATUS BAR ── */}
      <div className="xex-status-bar" style={{ background:"#1d6f42", padding:"2px 14px", display:"flex", alignItems:"center", gap:14, fontSize:10.5, color:"rgba(255,255,255,.7)", flexShrink:0 }}>
        <span>Listo</span>
        {activeCell && <>
          <span>Celda: <b style={{color:"white"}}>{activeCell}</b></span>
          <span>Valor: <b style={{color:"white"}}>{displayVal(activeCell)||"(vacío)"}</b></span>
          {LOCKED.has(activeCell) && <span style={{color:"#fde68a"}}>🔒 Celda bloqueada</span>}
          {!LOCKED.has(activeCell) && cells[activeCell]?.startsWith("=") && <span style={{color:"#86efac"}}>Fórmula ✓</span>}
        </>}
        <span style={{marginLeft:"auto"}}>Zoom 100%</span>
      </div>

      {/* ── INTRO MODAL ── */}
      {introTransition.shouldRender && (
        <div style={{
          position:"fixed",inset:0,background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:999,
          animation:`${introTransition.closing?"xexBackdropOut":"xexBackdropIn"} .26s ease forwards`,
        }}>
          <div style={{
            background:"white",borderRadius:16,padding:"34px 38px",maxWidth:430,width:"90%",boxShadow:"0 24px 60px rgba(0,0,0,.3)",
            animation:`${introTransition.closing?"xexModalOut":"xexModalIn"} .3s cubic-bezier(.34,1.56,.64,1) forwards`,
          }}>
            <div style={{ fontSize:40, marginBottom:10 }}>{config.badge}</div>
            <div style={{ fontSize:19,fontWeight:800,color:"#1d6f42",marginBottom:7 }}>{config.introTitle}</div>
            <div style={{ fontSize:12.5,color:"#475569",lineHeight:1.7,marginBottom:14 }}>{config.description}</div>
            {config.introFunctions?.length > 0 && (
              <div style={{ background:"#f0fdf4",borderRadius:10,padding:"11px 13px",marginBottom:18 }}>
                <div style={{ fontSize:10,fontWeight:700,color:"#166534",marginBottom:6,textTransform:"uppercase",letterSpacing:.8 }}>
                  Funciones que aprenderás
                </div>
                {config.introFunctions.map(([fn,desc])=>(
                  <div key={fn} style={{ display:"flex",gap:9,marginBottom:4,fontSize:11.5,alignItems:"center" }}>
                    <div style={{width: "fit-content"}}>
                      <code style={{ background:"#dcfce7",color:"#166534",padding:"1px 6px",borderRadius:4,fontFamily:"monospace",fontSize:10.5,whiteSpace:"nowrap",flexShrink:0 }}>{fn}</code>
                      <p style={{ color:"#475569" }}>{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button onClick={()=>{
              setShowIntro(false);
              if (!getExcelTourSeen()) { markExcelTourSeen(); setTimeout(() => setTourRun(true), 350); }
            }} style={{ background:"#1d6f42",color:"white",border:"none",borderRadius:9,padding:"11px 0",fontSize:13.5,fontWeight:700,cursor:"pointer",width:"100%" }}>
              ¡Empezar ejercicio! →
            </button>
          </div>
        </div>
      )}

      {/* ── CELEBRATION MODAL ── */}
      {celebrationTransition.shouldRender && (
        <div onClick={()=>setShowCelebration(false)} style={{
          position:"fixed",inset:0,background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:999,
          animation:`${celebrationTransition.closing?"xexBackdropOut":"xexBackdropIn"} .26s ease forwards`,
        }}>
          <div onClick={e=>e.stopPropagation()} style={{
            background:"white",borderRadius:16,padding:"42px 48px",textAlign:"center",boxShadow:"0 24px 70px rgba(0,0,0,.35)",maxWidth:380,
            animation:`${celebrationTransition.closing?"xexModalOut":"xexModalIn"} .32s cubic-bezier(.34,1.56,.64,1) forwards`,
          }}>
            <div style={{ fontSize:56,marginBottom:8 }}>🏆</div>
            <div style={{ fontSize:22,fontWeight:900,color:"#1d6f42",marginBottom:6 }}>¡Ejercicio Completado!</div>
            <div style={{ fontSize:12.5,color:"#64748b",marginBottom:18,lineHeight:1.6 }}>{config.completionText}</div>
            <div style={{ background:"linear-gradient(135deg,#1d6f42,#059669)",borderRadius:12,padding:"13px 24px",color:"white",fontWeight:800,fontSize:20,marginBottom:20 }}>
              +{totalXP} XP 🌟
            </div>
            <button onClick={handleExit} style={{ background:"#1d6f42",color:"white",border:"none",borderRadius:8,padding:"11px 32px",fontSize:13.5,fontWeight:700,cursor:"pointer" }}>
              Terminar →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
//  DEMO — Selector de ejercicios
// ═══════════════════════════════════════════════════════════════════════════════
const EXERCISES = [
  { key:"basico",    label:"🧮 Fórmulas Básicas",    tag:"Básico",       config: EJERCICIO_BASICO },
  { key:"dashboard", label:"📊 Dashboard de Ventas", tag:"Intermedio",   config: EJERCICIO_DASHBOARD },
  { key:"si",        label:"🔀 Función SI()",         tag:"Avanzado",     config: EJERCICIO_SI },
];

export default function App() {
  const [selected, setSelected] = useState("basico");
  const [showPicker, setShowPicker] = useState(true);

  const current = EXERCISES.find(e=>e.key===selected);

  if (showPicker) {
    return (
      <div style={{ fontFamily:"'Segoe UI',system-ui,sans-serif", background:"#f0f4f8", minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
        <div style={{ maxWidth:480, width:"100%" }}>
          {/* Header */}
          <div style={{ textAlign:"center", marginBottom:32 }}>
            <div style={{ display:"inline-flex", alignItems:"center", gap:10, background:"#1d6f42", color:"white", padding:"6px 16px", borderRadius:99, fontSize:13, fontWeight:700, marginBottom:16 }}>
              <span style={{ fontWeight:900, fontSize:16 }}>X</span> Plataforma de Cursos Excel
            </div>
            <div style={{ fontSize:24, fontWeight:900, color:"#1e293b", marginBottom:6 }}>Elige tu ejercicio</div>
            <div style={{ fontSize:13.5, color:"#64748b" }}>Selecciona el nivel que quieres practicar hoy</div>
          </div>

          {/* Cards */}
          {EXERCISES.map(ex=>(
            <div key={ex.key}
              onClick={()=>setSelected(ex.key)}
              style={{
                background:"white", borderRadius:12, padding:"18px 20px", marginBottom:12,
                border:`2px solid ${selected===ex.key?"#1d6f42":"#e2e8f0"}`,
                cursor:"pointer", transition:"all .2s",
                boxShadow: selected===ex.key?"0 4px 20px rgba(29,111,66,.15)":"0 1px 4px rgba(0,0,0,.06)",
              }}
            >
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:28 }}>{ex.label.split(" ")[0]}</span>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:2 }}>
                    <span style={{ fontSize:14, fontWeight:700, color:"#1e293b" }}>{ex.label.slice(3)}</span>
                    <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:99, background: ex.tag==="Básico"?"#dbeafe":ex.tag==="Intermedio"?"#dcfce7":"#f3e8ff", color: ex.tag==="Básico"?"#1e40af":ex.tag==="Intermedio"?"#166534":"#6b21a8" }}>{ex.tag}</span>
                  </div>
                  <div style={{ fontSize:11.5, color:"#64748b" }}>{ex.config.description.slice(0,80)}…</div>
                </div>
                <div style={{ width:20, height:20, borderRadius:"50%", border:`2px solid ${selected===ex.key?"#1d6f42":"#cbd5e1"}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  {selected===ex.key && <div style={{ width:10, height:10, borderRadius:"50%", background:"#1d6f42" }}/>}
                </div>
              </div>
            </div>
          ))}

          <button
            onClick={()=>setShowPicker(false)}
            style={{ width:"100%", background:"#1d6f42", color:"white", border:"none", borderRadius:10, padding:"14px 0", fontSize:15, fontWeight:700, cursor:"pointer", marginTop:8 }}
          >
            Comenzar — {current?.label} →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position:"relative", height:"100vh" }}>
      {/* Back button */}
      <button
        onClick={()=>setShowPicker(true)}
        style={{ position:"absolute", top:9, right:160, zIndex:50, background:"rgba(255,255,255,.15)", border:"1px solid rgba(255,255,255,.3)", borderRadius:6, padding:"3px 11px", fontSize:11, color:"white", cursor:"pointer", fontWeight:600 }}
      >
        ← Cambiar ejercicio
      </button>
      <ExcelExerciseTemplate config={current.config}/>
    </div>
  );
}

// ─── EXPORTACIONES PARA USO EXTERNO ────────────────────────────────────────────
// Si usas este template en tu plataforma, importa lo que necesites:
//
//   import ExcelExerciseTemplate from "./ExcelExerciseTemplate";
//   import { EJERCICIO_BASICO, EJERCICIO_DASHBOARD, EJERCICIO_SI } from "./ExcelExerciseTemplate";
//
// Para crear un nuevo ejercicio, copia cualquiera de los EJERCICIO_* y
// modifica las propiedades según el schema documentado al inicio del archivo.
export { ExcelExerciseTemplate, EJERCICIO_BASICO, EJERCICIO_DASHBOARD, EJERCICIO_SI };
